import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { SharedPrizePoolIcon } from '@sorare/core/src/atoms/icons/SharedPrizePoolIcon';
import { Target } from '@sorare/core/src/atoms/icons/Target';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BrandT,
  BrandXS,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Position } from '@sorare/core/src/components/CornerBadge';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { playerDetails } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import type { Color } from '@sorare/core/src/style/types';

import { TaskCampaignLabel } from '#src/components/missions/Streaks/TaskCampaignLabel/index.tsx';
import { isFixtureLive } from '#src/lib/so5.ts';

import { RewardWrapperHeader } from './RewardWrapper/RewardWrapperHeader/index.tsx';
import { RewardWrapper, WrapperType } from './RewardWrapper/index.tsx';
import { ThresholdIcon } from './ThresholdIcon/index.tsx';
import { ThresholdReward } from './ThresholdReward/index.tsx';
import { ThresholdRewardsCollapse } from './ThresholdRewardsCollapse/index.tsx';
import { ThresholdScore } from './ThresholdScore/index.tsx';
import { ThresholdsBar } from './ThresholdsBar/index.tsx';
import type { TrckGroupPVECompetitionPrizePool_so5Leaderboard } from './__generated__/index.graphql.ts';
import { taskThresholdscColors } from './utils.ts';

const BottomAlignedSBHorizontal = styled(SBHorizontal)`
  align-items: flex-end;
`;
const FullWidthChildrenHorizontal = styled(Horizontal)`
  align-items: stretch;
  & > * {
    flex: 1;
  }
`;
const CornerBadgeWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  position: absolute;
  align-items: flex-start;
  right: calc(-1 * var(--half-unit) - 1px);
  top: var(--unit);
`;

type Props = {
  so5Leaderboard?: TrckGroupPVECompetitionPrizePool_so5Leaderboard;
};
export const TrckGroupPVECompetitionPrizePool = ({ so5Leaderboard }: Props) => {
  const { so5Fixture, myTasks, rewardsConfig } = so5Leaderboard || {};
  const [task] = myTasks || [];

  if (!so5Fixture || !task || !isType(task, 'ThresholdsStreakTask')) {
    return null;
  }

  const { scoreTaskCampaign, rewardTaskCampaign, thresholds, so5Lineups } =
    task;

  const currentThresholdIndex = thresholds.findIndex(
    ({ current }) => !!current
  );
  const currentThreshold = thresholds[currentThresholdIndex];
  const currentStreakToDisplay = currentThresholdIndex + 1;
  const { color, rgbColor } =
    taskThresholdscColors[currentThresholdIndex] || {};

  const thresholdReward = currentThreshold.rewardConfigs.at(0);
  const thresholdOriginalReward = currentThreshold.originalRewardConfigs?.at(0);
  const sharePoolSo5RewardConfig = rewardsConfig?.conditional?.at(0);
  const sharePoolReward = sharePoolSo5RewardConfig?.rewardConfigs?.at(0);
  const additionalLineupTasksCompleted =
    currentThreshold.additionalLineupTasks.filter(
      ({ aasmState }) => aasmState === ManagerProgressionTaskState.COMPLETED
    );

  const sharedPoolRgbColor = '201, 88, 242';
  const sharedPoolGradient =
    'linear-gradient(82deg, #CC8CD6 -11.67%, #BE92D4 32.51%, #A28BD7 54.72%, #98DDF4 100%)';

  const isLive = isFixtureLive(so5Fixture);
  const bestLineupScore = so5Lineups?.reduce((bestScore, so5Lineup) => {
    const bestSo5Ranking = sortBy(
      ({ score }) => score,
      so5Lineup.so5Rankings
    ).at(-1);

    if (bestSo5Ranking && bestSo5Ranking.score > bestScore) {
      return bestSo5Ranking.score;
    }
    return bestScore;
  }, 0);
  const disabled =
    isLive && task.aasmState !== ManagerProgressionTaskState.COMPLETED;

  return (
    <Vertical gap={3}>
      {!!isLive && (
        <BottomAlignedSBHorizontal>
          <Vertical>
            <BrandXS color={task.color as Color}>
              <FormattedMessage
                id="TrckGroupPVECompetitionPrizePool.header.current"
                defaultMessage="Current"
              />
            </BrandXS>
            <BrandT>
              <FormattedMessage
                id="TrckGroupPVECompetitionPrizePool.header.highestScore"
                defaultMessage="Highest score"
              />
            </BrandT>
          </Vertical>
          <Horizontal gap={0.5}>
            {!!bestLineupScore && (
              <BrandT>
                <FormattedNumber value={bestLineupScore} />
              </BrandT>
            )}
            <BrandXS color="var(--c-nd-600)">
              <FormattedMessage {...playerDetails.points} />
            </BrandXS>
          </Horizontal>
        </BottomAlignedSBHorizontal>
      )}
      <Vertical gap={3}>
        <RewardWrapper type={WrapperType.PRIMARY} rgbColor={rgbColor}>
          <CornerBadgeWrapper>
            {[rewardTaskCampaign, scoreTaskCampaign].map(
              taskCampaign =>
                taskCampaign && (
                  <TaskCampaignLabel
                    key={taskCampaign.slug}
                    align={Position.RIGHT}
                    variant="cornerBadge"
                    taskCampaign={taskCampaign}
                  />
                )
            )}
          </CornerBadgeWrapper>
          <Vertical gap={2}>
            <Horizontal>
              <Target colorStop1={color} colorStop2={color} />
              <LabelL bold>
                <FormattedMessage
                  id="TrckGroupPVECompetitionPrizePool.targetLvl"
                  defaultMessage="Target LVL {i}"
                  values={{ i: currentStreakToDisplay }}
                />
              </LabelL>
              <ThresholdScore
                color={color}
                thresholdLowered={!!scoreTaskCampaign}
                threshold={currentThreshold}
              />
            </Horizontal>
            <ThresholdsBar
              currentThresholdIndex={currentThresholdIndex}
              thresholds={task.thresholds}
              currentBestScore={isLive ? bestLineupScore : undefined}
            />
            <ThresholdRewardsCollapse
              color={color}
              scoreTaskCampaign={scoreTaskCampaign}
              currentThresholdIndex={currentThresholdIndex}
              thresholds={thresholds}
            />
          </Vertical>
        </RewardWrapper>
        <FullWidthChildrenHorizontal>
          {!!thresholdReward && (
            <RewardWrapper
              type={WrapperType.SECONDARY}
              disabled={disabled}
              rgbColor={rgbColor}
              header={
                <RewardWrapperHeader rgbColor={rgbColor}>
                  <Target colorStop1={color} colorStop2={color} />
                  <ThresholdScore
                    color={color}
                    thresholdLowered={!!scoreTaskCampaign}
                    threshold={currentThreshold}
                  />
                </RewardWrapperHeader>
              }
            >
              <Vertical gap={1.5} center>
                <ThresholdReward
                  vertical
                  anyRewardConfig={thresholdReward}
                  originalAnyRewardConfig={thresholdOriginalReward}
                  rewardTaskCampaign={rewardTaskCampaign}
                />
                <Horizontal gap={0.5}>
                  <ThresholdIcon
                    currentStreakToDisplay={currentStreakToDisplay}
                  />
                  <LabelS>
                    <FormattedMessage
                      id="TrckGroupPVECompetitionPrizePool.firstLineupRewards"
                      defaultMessage="First Lineup"
                    />
                  </LabelS>
                </Horizontal>
              </Vertical>
            </RewardWrapper>
          )}
          {!!currentThreshold.uniqueAdditionalLineupRewardConfig && (
            <RewardWrapper
              type={WrapperType.SECONDARY}
              disabled={!!isLive && !additionalLineupTasksCompleted.length}
              rgbColor={rgbColor}
              header={
                <RewardWrapperHeader rgbColor={rgbColor}>
                  <Target colorStop1={color} colorStop2={color} />
                  <ThresholdScore
                    color={color}
                    thresholdLowered={!!scoreTaskCampaign}
                    threshold={currentThreshold}
                  />
                </RewardWrapperHeader>
              }
            >
              <Vertical gap={1.5} center>
                <ThresholdReward
                  vertical
                  anyRewardConfig={
                    currentThreshold.uniqueAdditionalLineupRewardConfig
                  }
                  originalAnyRewardConfig={
                    currentThreshold.originalUniqueAdditionalLineupRewardConfig
                  }
                  rewardTaskCampaign={rewardTaskCampaign}
                />
                <Horizontal gap={0.5}>
                  <ThresholdIcon
                    currentStreakToDisplay={currentStreakToDisplay}
                    additionalLineupReward
                  />
                  <LabelS>
                    {isLive ? (
                      <FormattedMessage
                        id="TrckGroupPVECompetitionPrizePool.additionalLineupRewardsLive"
                        defaultMessage="{i} additional {i, plural, =0 {lineup} one {lineup} other {lineups}}"
                        values={{ i: additionalLineupTasksCompleted.length }}
                      />
                    ) : (
                      <FormattedMessage
                        id="TrckGroupPVECompetitionPrizePool.additionalLineupRewards"
                        defaultMessage="Per additional Lineup"
                      />
                    )}
                  </LabelS>
                </Horizontal>
              </Vertical>
            </RewardWrapper>
          )}
        </FullWidthChildrenHorizontal>
        {!!sharePoolReward && !!sharePoolSo5RewardConfig?.score && (
          <RewardWrapper
            type={WrapperType.SECONDARY}
            disabled={(bestLineupScore || 0) < sharePoolSo5RewardConfig.score}
            rgbColor={sharedPoolRgbColor}
            header={
              <RewardWrapperHeader rgbColor={rgbColor}>
                <Target colorStop1="#CC8CD6" colorStop2="#98DDF4" />
                <ThresholdScore
                  color={sharedPoolGradient}
                  score={sharePoolSo5RewardConfig?.score}
                />
              </RewardWrapperHeader>
            }
          >
            <Vertical center>
              <ThresholdReward vertical anyRewardConfig={sharePoolReward} />
              <Horizontal>
                <SharedPrizePoolIcon />
                <LabelS
                  className="text-gradient-color"
                  style={
                    {
                      '--text-gradient-color': sharedPoolGradient,
                    } as CSSProperties
                  }
                >
                  <FormattedMessage
                    id="TrckGroupPVECompetitionPrizePool.sharedPool"
                    defaultMessage="Shared prize pool"
                  />
                </LabelS>
              </Horizontal>
            </Vertical>
          </RewardWrapper>
        )}
      </Vertical>
    </Vertical>
  );
};

TrckGroupPVECompetitionPrizePool.fragments = {
  so5Leaderboard: gql`
    fragment TrckGroupPVECompetitionPrizePool_so5Leaderboard on So5Leaderboard {
      slug
      so5Fixture {
        slug
        ...isFixtureLive_so5Fixture
      }
      myTasks {
        id
        aasmState
        target
        progress
        color
        ... on ThresholdsStreakTask {
          id
          so5Lineups {
            id
            so5Rankings {
              id
              score
            }
          }
          thresholds {
            current
            additionalLineupTasks {
              id
              aasmState
            }
            originalUniqueAdditionalLineupRewardConfig {
              id
              ...ThresholdReward_anyRewardConfig
            }
            uniqueAdditionalLineupRewardConfig {
              id
              ...ThresholdReward_anyRewardConfig
            }
            originalRewardConfigs {
              id
              ...ThresholdReward_anyRewardConfig
            }
            rewardConfigs {
              id
              ...ThresholdReward_anyRewardConfig
            }
            ...ThresholdsBar_thresholdStreakTaskThreshold
            ...ThresholdScore_thresholdStreakTaskThreshold
          }
          scoreTaskCampaign {
            slug
            ...TaskCampaignLabel_taskCampaign
            ...ThresholdRewardsCollapse_taskCampaign
          }
          rewardTaskCampaign {
            slug
            ...ThresholdReward_TaskCampaign
            ...TaskCampaignLabel_taskCampaign
          }
        }
      }
      rewardsConfig {
        conditional {
          id
          score
          rewardConfigs {
            id
            ...ThresholdReward_anyRewardConfig
          }
        }
      }
    }
    ${ThresholdsBar.fragments.thresholdStreakTaskThreshold}
    ${ThresholdScore.fragments.thresholdStreakTaskThreshold}
    ${ThresholdReward.fragments.taskCampaign}
    ${ThresholdReward.fragments.anyRewardConfig}
    ${TaskCampaignLabel.fragments.taskCampaign}
    ${isFixtureLive.fragments.so5Fixture}
    ${ThresholdRewardsCollapse.fragments.taskCampaign}
  ` as TypedDocumentNode<TrckGroupPVECompetitionPrizePool_so5Leaderboard>,
};
